import { Upload, Clipboard } from 'lucide-react';
import { useId, useRef, useState, type ClipboardEvent, type ReactNode } from 'react';
import { Button } from './Button';
import { cn } from './cn';

function pasteFiles(event: ClipboardEvent, onFiles: (files: File[]) => void) {
  const files = Array.from(event.clipboardData.files).filter((file) => file.type.startsWith('image/'));
  if (!files.length) return; // Обычный текст продолжает вставляться в поле.
  event.preventDefault();
  onFiles(files);
}

export function FilePasteArea({
  children,
  onFiles,
  label,
}: {
  children: ReactNode;
  onFiles: (files: File[]) => void;
  label: string;
}) {
  return (
    <div data-slot="file-paste-area" aria-label={label} onPaste={(e) => pasteFiles(e, onFiles)}>
      {children}
    </div>
  );
}

/** Область принимает файлы, но не загружает их: транспорт и проверка принадлежат сервису. */
export function FileDropzone({
  onFiles,
  disabled = false,
  label = 'Прикрепить файлы',
}: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  label?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const depth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const hint = useId();
  return (
    <div
      data-slot="file-dropzone"
      data-dragging={dragging || undefined}
      tabIndex={disabled ? -1 : 0}
      role="group"
      aria-label={label}
      aria-describedby={hint}
      aria-disabled={disabled}
      className={cn(
        'rounded-md border border-dashed p-5 transition-colors duration-fast focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        dragging ? 'border-brand bg-brand-soft' : 'border-line-strong bg-canvas',
      )}
      onPaste={(event) => {
        if (!disabled) pasteFiles(event, onFiles);
      }}
      onDragEnter={(event) => {
        if (disabled || !event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        depth.current++;
        setDragging(true);
      }}
      onDragOver={(event) => {
        if (!disabled && event.dataTransfer.types.includes('Files')) {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        }
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        depth.current = Math.max(0, depth.current - 1);
        if (!depth.current) setDragging(false);
      }}
      onDrop={(event) => {
        if (disabled || !event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        event.stopPropagation();
        depth.current = 0;
        setDragging(false);
        const files = Array.from(event.dataTransfer.files);
        if (files.length) onFiles(files);
      }}
    >
      <input
        ref={input}
        type="file"
        multiple
        disabled={disabled}
        className="sr-only"
        tabIndex={-1}
        aria-label={label}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (files.length) onFiles(files);
          event.target.value = '';
        }}
      />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Upload className="size-5 shrink-0 text-fg-muted" aria-hidden />
          <div>
            <p className="type-subheading">Перетащи файлы сюда</p>
            <p id={hint} className="mt-1 text-xs text-fg-muted">
              <Clipboard className="mr-1 inline size-3.5" aria-hidden />
              Или вставь скриншот из буфера
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled}
          onClick={() => input.current?.click()}
        >
          Выбрать файлы
        </Button>
      </div>
    </div>
  );
}
